import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  agreementStatusInfo,
  chargeLabel,
  cycleLabel,
  cycleSuffix,
  diffDays,
  formatRate,
  type ChargeType,
  type InvoiceStatus,
  type LeaseStatus,
  type RentCycle,
  termSuffix,
  toTerm,
} from "@rentorng/core";
import { api } from "@/lib/api";
import { titleCase, useFormat, useToday } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { ExpiryBadge, InvoiceStatusBadge, LeaseStatusBadge, MessageStatusBadge } from "@/components/status";
import { Alert, Button, Can, Card, CardTitle, Detail, EmptyState, ErrorState, Field, Input, LinkButton, PageHeader, Select, Spinner, Table } from "@/components/ui";
import { RaiseAgreement } from "./Agreements";
import { MoneyInput } from "@/components/money-input";
import { beneficiaryNameFor, useBeneficiaries } from "@/components/beneficiary-picker";

interface LeaseResponse {
  lease: {
    id: string;
    reference: string;
    status: string;
    startDate: string;
    endDate: string | null;
    rentAmount: number;
    rentCycle: string;
    rentCycleEvery?: number;
    paymentTerms: string;
    graceDays: number;
    /** The day the rent is to be looked at again. Null on every tenancy nobody has agreed one for. */
    reviewDate: string | null;
    depositAmount: number;
    depositHeld: number;
    /* `label` names an "Other" charge and is absent on every other type, and on every charge
       stored before the field existed — which is why it is optional rather than nullable here and
       why nothing had to be back-filled. `chargeLabel` reads a missing one as "Other". */
    charges: Array<{
      type: string;
      label?: string | null;
      basis: string;
      value: number;
      frequency: string;
      /** Who this charge's money is for. Null on a charge the agency has not attributed. */
      beneficiaryId?: string | null;
    }> | null;
    notes: string | null;
    terminatedOn: string | null;
    terminationReason: string | null;
    renewedFromId: string | null;
  };
  tenant: { id: string; name: string; phone: string | null; email: string | null };
  unit: { id: string; label: string; status: string };
  property: { id: string; name: string; addressLine: string | null };
  derivedStatus: string;
  invoices: Array<{ id: string; number: string; dueDate: string; total: number; paid: number; status: string }>;
  messages: Array<{ id: string; channel: string; status: string; subject: string | null; trigger: string | null; createdAt: string; sentAt: string | null }>;
  /** Every agreement raised from this tenancy, newest first, with how far each has got. */
  agreements: Array<{
    id: string;
    reference: string;
    status: string;
    issuedOn: string;
    completedAt: string | null;
    signed: number;
    parties: number;
    signing: { total: number; signed: number; outstanding: number; complete: boolean; summary: string };
    /** True once it has left draft: its copy of these terms is fixed and editing below will not move it. */
    frozen: boolean;
  }>;
  today: string;
  graceDays: number;
}

export function LeaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { vocab, can } = useSession();
  /* The charge rows carry an id and not a name, so the list is fetched once here rather than
     denormalised onto every tenancy. It is the same query key every picker uses, so a beneficiary
     added on the form that made this tenancy is already in the cache. */
  const { data: beneficiaries } = useBeneficiaries();
  const fmt = useFormat();
  const today = useToday();
  const [panel, setPanel] = useState<"none" | "renew" | "terminate">("none");
  /** The one term on this screen that is edited in place, because it is the one agreed last. */
  const [editingReview, setEditingReview] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["lease", id],
    queryFn: () => api.get<LeaseResponse>(`/leases/${id}`),
    enabled: Boolean(id),
  });

  const renew = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>(`/leases/${id}/renew`, input),
    invalidates: [["leases"], ["lease", id ?? ""], ["invoices"], ["dashboard"]],
    onSuccess: (result) => navigate(`/app/tenancies/${result.id}`),
  });

  /**
   * Setting or clearing the rent review date.
   *
   * On this screen rather than only on the form that creates a tenancy, because a review date is
   * the term most often agreed after the fact — a clause read back off the signed agreement a week
   * later, or a date somebody agreed on the telephone. A field that can only be filled in at the
   * moment of creation is a field that stays empty.
   *
   * An empty box clears it, and that has to work: a date entered against the wrong tenancy would
   * otherwise be reported as due for ever with no way to take it off.
   */
  const setReview = useApiMutation({
    mutationFn: (input: { reviewDate: string | null }) => api.patch(`/leases/${id}`, input),
    invalidates: [["lease", id ?? ""], ["leases"], ["reports"]],
    onSuccess: () => setEditingReview(false),
  });

  const terminate = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/leases/${id}/terminate`, input),
    invalidates: [["leases"], ["lease", id ?? ""], ["units"], ["invoices"], ["dashboard"]],
    onSuccess: () => setPanel("none"),
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { lease, tenant, unit, property } = data;
  const outstanding = data.invoices
    .filter((invoice) => !["void", "written_off", "paid"].includes(invoice.status))
    .reduce((sum, invoice) => sum + (invoice.total - invoice.paid), 0);
  const daysLeft = lease.endDate ? diffDays(data.today, lease.endDate) : null;
  const live = ["active", "expiring", "expired"].includes(lease.status);

  return (
    <div>
      <PageHeader
        title={`${tenant.name} · ${unit.label}`}
        back={{ to: "/app/tenancies", label: titleCase(vocab.leases) }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span>{lease.reference}</span>
            <LeaseStatusBadge
              lease={{ status: lease.status as LeaseStatus, startDate: lease.startDate, endDate: lease.endDate }}
              asOf={data.today}
            />
            <ExpiryBadge endDate={lease.endDate} asOf={data.today} />
            <Link to={`/app/properties/${property.id}`} className="text-ink-500 hover:underline">
              {property.name}
            </Link>
          </span>
        }
        actions={
          <>
            {can("reminder.send") ? (
              <LinkButton to={`/app/reminders/send?leaseId=${lease.id}`} variant="secondary" need="reminder.send">
                Send a reminder
              </LinkButton>
            ) : null}
            {can("lease.edit") && live ? (
              <Button variant="secondary" onClick={() => setPanel(panel === "renew" ? "none" : "renew")}>
                Renew
              </Button>
            ) : null}
            {can("lease.terminate") && live ? (
              <Button variant="danger" onClick={() => setPanel(panel === "terminate" ? "none" : "terminate")}>
                Terminate
              </Button>
            ) : null}
            {can("payment.record") ? (
              <LinkButton to={`/app/payments/new?partyId=${tenant.id}&leaseId=${lease.id}`} need="payment.record">Record a payment</LinkButton>
            ) : null}
          </>
        }
      />

      {panel === "renew" ? (
        <Card className="mb-5">
          <CardTitle hint={`The current ${vocab.lease} is marked renewed and a new one starts the day after it ends. The caution deposit is not re-charged.`}>
            Renew this {vocab.lease}
          </CardTitle>
          {renew.errorMessage ? <Alert tone="danger">{renew.errorMessage}</Alert> : null}
          <form
            className="mt-3 grid gap-4 sm:grid-cols-4"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              renew.mutate({
                startDate: String(form.get("startDate") || ""),
                cycles: Number(form.get("cycles") || 1),
                increasePercent: form.get("increasePercent") ? Number(form.get("increasePercent")) : undefined,
                rentAmount: form.get("rentAmount") ? String(form.get("rentAmount")) : undefined,
              });
            }}
          >
            <Field label="Starts" htmlFor="startDate">
              <Input id="startDate" name="startDate" type="date" defaultValue={lease.endDate ? addOneDay(lease.endDate) : today} />
            </Field>
            <Field label="For how many terms" htmlFor="cycles">
              <Input id="cycles" name="cycles" inputMode="numeric" placeholder="1" defaultValue="1" />
            </Field>
            <Field label="Increase %" htmlFor="increasePercent" hint="Or set a new rent instead.">
              <Input id="increasePercent" name="increasePercent" inputMode="decimal" placeholder="10" />
            </Field>
            <Field label={`New ${vocab.rent}`} htmlFor="rentAmount" hint="Overrides the percentage.">
              <MoneyInput id="rentAmount" name="rentAmount" placeholder={String(lease.rentAmount / 100)} />
            </Field>
            <div className="sm:col-span-4">
              <Button type="submit" disabled={renew.isPending}>
                {renew.isPending ? "Renewing…" : `Renew ${vocab.lease}`}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {panel === "terminate" ? (
        <Card className="mb-5">
          <CardTitle hint="Invoices for periods that had not started are voided. Money owed for time already occupied stays owed.">
            Terminate this {vocab.lease}
          </CardTitle>
          {terminate.errorMessage ? <Alert tone="danger">{terminate.errorMessage}</Alert> : null}
          {terminate.isSuccess ? <Alert tone="success">Terminated. The {vocab.unit} is available again.</Alert> : null}
          <form
            className="mt-3 grid gap-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              terminate.mutate({
                terminatedOn: String(form.get("terminatedOn") || today),
                reason: String(form.get("reason") || ""),
                unitStatus: String(form.get("unitStatus") || "vacant"),
                voidFutureInvoices: true,
              });
            }}
          >
            <Field label="Exit date" htmlFor="terminatedOn" required>
              <Input id="terminatedOn" name="terminatedOn" type="date" defaultValue={today} required />
            </Field>
            <Field label="Reason" htmlFor="reason">
              <Input id="reason" name="reason" placeholder="Tenant moved out" />
            </Field>
            <Field label={`${titleCase(vocab.unit)} becomes`} htmlFor="unitStatus">
              <Select id="unitStatus" name="unitStatus" defaultValue="vacant">
                <option value="vacant">Vacant</option>
                <option value="renovation">Under renovation</option>
                <option value="off_market">Off market</option>
              </Select>
            </Field>
            <div className="sm:col-span-3">
              <Button type="submit" variant="danger" disabled={terminate.isPending}>
                {terminate.isPending ? "Terminating…" : "Terminate"}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="mb-5 grid gap-4 sm:grid-cols-4">
        <Card className="border-l-4 border-l-danger-500">
          <p className="text-xs uppercase tracking-wide text-ink-500">Outstanding</p>
          <p className="tabular mt-1 text-xl font-semibold">{fmt.money(outstanding)}</p>
        </Card>
        <Card className="border-l-4 border-l-brand-500">
          <p className="text-xs uppercase tracking-wide text-ink-500">{titleCase(vocab.rent)}</p>
          <p className="tabular mt-1 text-xl font-semibold">{fmt.money(lease.rentAmount)}</p>
          <p className="text-xs text-ink-500">{termSuffix(toTerm(lease.rentCycle as RentCycle, lease.rentCycleEvery as number))}</p>
        </Card>
        <Card className="border-l-4 border-l-info-600">
          <p className="text-xs uppercase tracking-wide text-ink-500">Deposit held</p>
          <p className="tabular mt-1 text-xl font-semibold">{fmt.money(lease.depositHeld)}</p>
          <p className="text-xs text-ink-500">of {fmt.money(lease.depositAmount)} agreed</p>
        </Card>
        <Card className="border-l-4 border-l-warn-500">
          <p className="text-xs uppercase tracking-wide text-ink-500">Ends</p>
          <p className="mt-1 text-xl font-semibold">{fmt.date(lease.endDate)}</p>
          <p className="text-xs text-ink-500">
            {daysLeft === null ? "Open-ended" : daysLeft < 0 ? `${Math.abs(daysLeft)} days ago` : `in ${daysLeft} days`}
          </p>
        </Card>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle action={<Link to={`/app/invoices?partyId=${tenant.id}`} className="link">All</Link>}>
                {titleCase(vocab.invoices)}
              </CardTitle>
            </div>
            {data.invoices.length === 0 ? (
              <EmptyState title="No invoices raised yet." />
            ) : (
              <Table
                head={
                  <tr>
                    <th>Number</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th className="num">Total</th>
                    <th className="num">Balance</th>
                  </tr>
                }
              >
                {data.invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <Link to={`/app/invoices/${invoice.id}`} className="link">
                        {invoice.number}
                      </Link>
                    </td>
                    <td className="text-ink-500">{fmt.date(invoice.dueDate)}</td>
                    <td>
                      <InvoiceStatusBadge
                        invoice={{ dueDate: invoice.dueDate, total: invoice.total, paid: invoice.paid, status: invoice.status as InvoiceStatus }}
                        asOf={data.today}
                        graceDays={data.graceDays}
                      />
                    </td>
                    <td className="num tabular">{fmt.money(invoice.total)}</td>
                    <td className="num tabular font-medium">{fmt.money(invoice.total - invoice.paid)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>

          {/*
            The agreements raised from this tenancy.

            It sits above the reminders and below the invoices because that is the order somebody
            works in: what is owed, what was signed, what was chased. The card is drawn even when
            empty — unlike most empty panels in this application — because "no agreement has been
            raised for this tenancy" is exactly the state an agency needs to be told about, and a
            missing card says nothing at all.
          */}
          <Card>
            <CardTitle
              action={<Link to="/app/agreements" className="link">All</Link>}
              hint="An agreement is generated from this tenancy, so every figure on it comes from the terms below. Once it has been sent, its terms are fixed — changing the tenancy afterwards does not change a document somebody has signed."
            >
              Agreements
            </CardTitle>

            {data.agreements.length === 0 ? (
              <p className="mb-1 text-sm text-ink-600">
                Nothing has been raised for this {vocab.lease} yet.
              </p>
            ) : (
              <Table head={<tr><th>Reference</th><th>Signed</th><th>State</th><th>Raised</th></tr>}>
                {data.agreements.map((agreement) => (
                  <tr key={agreement.id}>
                    <td>
                      <Link to={`/app/agreements/${agreement.id}`} className="link font-medium">
                        {agreement.reference}
                      </Link>
                    </td>
                    <td>{agreement.signing.summary}</td>
                    <td>{agreementStatusInfo(agreement.status).label}</td>
                    <td className="text-ink-500">{fmt.date(agreement.issuedOn)}</td>
                  </tr>
                ))}
              </Table>
            )}

            {/* The warning that has to be here rather than on the agreement's own page: this is
                where somebody presses Edit. */}
            {data.agreements.some((agreement) => agreement.frozen) ? (
              <Alert tone="info">
                An agreement raised from this {vocab.lease} has been sent out. Editing the terms below will not change
                it — a signed document says what it said. To agree new terms, raise a replacement agreement.
              </Alert>
            ) : null}

            <Can need="document.edit">
              <div className="mt-4">
                <RaiseAgreement leaseId={data.lease.id} existing={data.agreements.length} />
              </div>
            </Can>
          </Card>

          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle>Reminders sent</CardTitle>
            </div>
            {data.messages.length === 0 ? (
              <EmptyState title="Nothing has been sent about this tenancy." />
            ) : (
              <Table head={<tr><th>When</th><th>Subject</th><th>Channel</th><th>Status</th></tr>}>
                {data.messages.map((message) => (
                  <tr key={message.id}>
                    <td className="text-ink-500">{fmt.dateTime(message.sentAt ?? message.createdAt)}</td>
                    <td>{message.subject ?? message.trigger ?? "Message"}</td>
                    <td className="text-ink-500">{message.channel}</td>
                    <td><MessageStatusBadge status={message.status} /></td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardTitle>Terms</CardTitle>
            <dl>
              <Detail label={titleCase(vocab.tenant)}>
                <Link to={`/app/clients/${tenant.id}`} className="link">
                  {tenant.name}
                </Link>
              </Detail>
              <Detail label={titleCase(vocab.unit)}>{unit.label}</Detail>
              <Detail label={titleCase(vocab.property)}>
                <Link to={`/app/properties/${property.id}`} className="link">
                  {property.name}
                </Link>
              </Detail>
              <Detail label="Starts">{fmt.date(lease.startDate)}</Detail>
              <Detail label="Ends">{fmt.date(lease.endDate)}</Detail>
              <Detail label="Cycle">{cycleLabel(lease.rentCycle as RentCycle)}</Detail>
              <Detail label="Paid">{lease.paymentTerms === "advance" ? "In advance" : "In arrears"}</Detail>
              <Detail label="Grace">{lease.graceDays} days</Detail>
              {/*
                The review, named even when there is none.

                Drawn as "Not set" rather than left off the list, because the whole reason a review
                is missed is that nothing on any screen mentions it. A row that is absent asks no
                question; a row reading "Not set" beside a button asks one.
              */}
              <Detail label="Rent review">
                {lease.reviewDate ? fmt.date(lease.reviewDate) : <span className="text-ink-500">Not set</span>}
              </Detail>
              {lease.terminatedOn ? <Detail label="Terminated">{fmt.date(lease.terminatedOn)}</Detail> : null}
            </dl>

            <Can need="lease.edit">
              {editingReview ? (
                <form
                  className="mt-3 flex flex-wrap items-end gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const form = new FormData(event.currentTarget);
                    setReview.mutate({ reviewDate: String(form.get("reviewDate") || "") || null });
                  }}
                >
                  <Field label="Rent review" htmlFor="reviewDate" hint="Leave it empty to remove the date.">
                    <Input id="reviewDate" name="reviewDate" type="date" defaultValue={lease.reviewDate ?? ""} />
                  </Field>
                  <div className="flex gap-2 pb-1">
                    <Button type="submit" disabled={setReview.isPending}>
                      {setReview.isPending ? "Saving…" : "Save"}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => setEditingReview(false)}>
                      Cancel
                    </Button>
                  </div>
                  {setReview.errorMessage ? (
                    <div className="w-full">
                      <Alert tone="danger">{setReview.errorMessage}</Alert>
                    </div>
                  ) : null}
                </form>
              ) : (
                <button type="button" className="link mt-3 text-sm" onClick={() => setEditingReview(true)}>
                  {lease.reviewDate ? "Change the rent review date" : "Set a rent review date"}
                </button>
              )}
            </Can>
            {lease.notes ? <p className="mt-3 whitespace-pre-wrap text-sm text-ink-600">{lease.notes}</p> : null}
          </Card>

          {/*
            Charges, each with whose money it is.

            **This is the agency's own record and it is shown to nobody else.** The tenant owes one
            party one sum; the distribution happens afterwards, out of money that has already
            arrived, and naming a landlord on a page a tenant can reach would print a property
            owner's name beside their own address. So it is here, on the screen only staff see, and
            deliberately absent from the portal, the pay link and the public breakdown — an absence
            `tools/verify-beneficiary-privacy.mjs` asserts rather than leaves to be inferred. See
            ADR-0137.

            "Not yet decided" is drawn rather than left blank. A legal fee that names nobody is the
            ordinary state of a tenancy where the agency has not said who drew the agreement, and
            the payout run reports it as unattributed rather than guessing — a blank line would
            leave that looking like a screen that had not finished loading.
          */}
          <Card>
            <CardTitle hint="Applied when each invoice is raised, and who each part of it is collected for.">
              Charges
            </CardTitle>
            {/*
              The rent is not on this list, and its absence is deliberate.

              Rent has no stored beneficiary on the tenancy: it is resolved to the owner of the unit
              at the moment each invoice is raised, by the same function the payout run reads. A row
              here would have to work that out a second way in the browser, and ADR-0099 is the
              record of what two implementations of one money question cost. The owner is named on
              the record above, where it is a fact rather than a derivation.
            */}
            {!lease.charges?.length ? (
              <p className="text-sm text-ink-500">No charges besides the {vocab.rent}.</p>
            ) : (
            <ul className="space-y-2 text-sm" data-lease-charges>
              {(lease.charges ?? []).map((charge, index) => (
                <li key={index} className="flex items-baseline justify-between gap-3">
                  <span>
                    {chargeLabel(charge.type as ChargeType, charge.label)}
                    <span className="block text-xs text-ink-500">
                      For {beneficiaryNameFor(beneficiaries?.beneficiaries, charge.beneficiaryId) ?? "not yet decided"}
                    </span>
                  </span>
                  <span className="tabular text-ink-600">
                    {charge.basis === "percent_of_rent" ? formatRate(charge.value) : fmt.money(charge.value)}
                    <span className="ml-1 text-xs text-ink-400">{charge.frequency === "once" ? "once" : "each period"}</span>
                  </span>
                </li>
              ))}
            </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/** The day after a tenancy ends is when its renewal starts. */
function addOneDay(date: string): string {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}
